import { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { FileSpreadsheet, LayoutGrid, Lock, LockOpen, Map as MapIcon, Plus, Sparkles, X } from "lucide-react"
import type { AppShell } from "@/App"
import {
  confirmDay,
  downloadDocument,
  errorMessage,
  getPlan,
  listOrders,
  listVehicles,
  listWarehouses,
  scheduleDay,
  setPickupDay,
  type Order,
  type PlanView,
  type Vehicle,
  type Warehouse,
} from "@/lib/api"
import { day10, dayShort, dm, eur, hhmm, kg, m3, n, todayLisbon } from "@/lib/format"
import { AsyncButton, ErrorBox, inputCls, Loading, Note, Tag } from "@/components/kit"
import { Button } from "@/components/ui/button"
import { isOpen } from "@/pages/Orders"
import { RoutePanel } from "@/pages/RoutePanel"
import { SageTag } from "@/pages/Dispatch"
import { cn } from "@/lib/utils"

const toMin = (hm: string | null | undefined, fallback: number) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm ?? "")
  return m ? Number(m[1]) * 60 + Number(m[2]) : fallback
}

function Bar({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-6 text-[10px] text-muted-foreground">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
        <div className={cn("h-full rounded-full", value > max && max > 0 ? "bg-red-500" : pct > 90 ? "bg-amber-500" : "bg-accent")} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

/**
 * Planear o dia (despacho manual): um dia de cada vez. À esquerda as
 * encomendas já atribuídas no Despacho e ainda sem dia; escolhem-se as que
 * saem em cada veículo (entram no fim da rota) e no fim «Sugerir ordem» pede a
 * melhor ordem só dessas paragens. As de transportadora ganham dia de recolha.
 * Confirmar o dia, documentos e link do motorista ficam como no quadro.
 */
export function DayPlan({ shell }: { shell: AppShell }) {
  const [orders, setOrders] = useState<Order[] | null>(null)
  const [view, setView] = useState<PlanView | null>(null)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [day, setDay] = useState<string | null>(null)
  const [vehicleId, setVehicleId] = useState<string | null>(null)
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const canEdit = shell.role >= 5
  const today = todayLisbon()

  const load = useCallback(async () => {
    try {
      const [o, p, v, w] = await Promise.all([listOrders(shell), getPlan(shell), listVehicles(shell), listWarehouses(shell)])
      setOrders(o)
      setView(p)
      const active = v.filter((x) => x.active !== false)
      setVehicles(active)
      setWarehouse(w.find((x) => x.active !== false) ?? null)
      setVehicleId((cur) => cur ?? active[0]?.id ?? null)
    } catch (err) {
      setError(errorMessage(err))
    }
  }, [shell])
  useEffect(() => void load(), [load])

  const days = useMemo(() => {
    const list = [...new Set([...(view?.assignable_days ?? []), ...(view?.plan?.days ?? []).map((d) => day10(d))])].filter((d): d is string => !!d && d >= today).sort()
    return list
  }, [view, today])
  useEffect(() => {
    if (!day && days.length) setDay(days[0])
  }, [day, days])

  const byOrder = useMemo(() => new Map((orders ?? []).map((o) => [o.id, o])), [orders])
  const inRoute = useMemo(() => new Set((view?.stops ?? []).filter((s) => s.result !== "failed").map((s) => s.order_id)), [view])
  const open = useMemo(() => (orders ?? []).filter(isOpen), [orders])

  // Por agendar: frota sem rota; transportadora sem dia de recolha (ou com um dia que já passou).
  const fleetPending = useMemo(() => open.filter((o) => o.dispatch_kind === "fleet" && !inRoute.has(o.id)), [open, inRoute])
  const carrierPending = useMemo(() => open.filter((o) => o.dispatch_kind === "carrier" && (!o.pickup_day || day10(o.pickup_day)! < today)), [open, today])
  const carrierToday = useMemo(() => open.filter((o) => o.dispatch_kind === "carrier" && day && day10(o.pickup_day) === day), [open, day])

  const groups = useMemo(() => {
    const out: Array<{ key: string; title: string; vehicle: Vehicle | null; items: Order[] }> = []
    for (const v of vehicles) out.push({ key: v.id, title: v.name, vehicle: v, items: fleetPending.filter((o) => o.dispatch_vehicle_id === v.id) })
    out.push({ key: "any", title: "Frota — veículo por escolher", vehicle: null, items: fleetPending.filter((o) => !o.dispatch_vehicle_id || !vehicles.some((v) => v.id === o.dispatch_vehicle_id)) })
    return out.filter((g) => g.items.length)
  }, [vehicles, fleetPending])

  const carrierGroups = useMemo(() => {
    const out = new Map<string, { id: string; name: string; items: Order[]; cost: number }>()
    for (const o of carrierToday) {
      const opt = o.options.find((x) => x.service_id === o.carrier_service_id)
      const id = opt?.carrier_id ?? "?"
      const g = out.get(id) ?? { id, name: opt?.carrier_name ?? "Transportadora", items: [], cost: 0 }
      g.items.push(o)
      g.cost += n(opt?.cost) ?? 0
      out.set(id, g)
    }
    return [...out.values()]
  }, [carrierToday])

  if (error) return <div className="p-4"><ErrorBox error={error} /></div>
  if (!orders || !view) return <Loading />

  const dayRoutes = view.routes.filter((r) => day10(r.day) === day)
  const confirmed = dayRoutes.length > 0 && dayRoutes.every((r) => r.status === "confirmed")
  const problems = dayRoutes.some((r) => r.problems?.length)
  const route = dayRoutes.find((r) => r.vehicle_id === vehicleId) ?? null
  const vehicle = vehicles.find((v) => v.id === vehicleId) ?? null

  const toggle = (id: string) => setChecked((p) => { const next = new Set(p); if (next.has(id)) next.delete(id); else next.add(id); return next })

  const run = async (title: string, fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      await load()
      shell.toast("success", title)
    } catch (err) {
      shell.toast("error", title, errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const addTo = (vid: string, ids: string[]) => {
    if (!day || !ids.length) return
    const v = vehicles.find((x) => x.id === vid)
    return run(`${ids.length} encomenda(s) em ${v?.name ?? "veículo"} · ${dayShort(day)}`, async () => {
      for (let i = 0; i < ids.length; i += 40) await scheduleDay(shell, { day, vehicle_id: vid, add: ids.slice(i, i + 40) })
      setChecked((p) => { const next = new Set(p); ids.forEach((id) => next.delete(id)); return next })
      setVehicleId(vid)
    })
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-card px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <b className="text-[14px]">Planear o dia</b>
          <select className={cn(inputCls, "h-8 w-auto")} value={day ?? ""} onChange={(e) => setDay(e.target.value)}>
            {days.map((d) => <option key={d} value={d}>{dayShort(d)}{d === today ? " (hoje)" : ""}</option>)}
          </select>
          <span className="text-[12.5px] text-muted-foreground">
            {dayRoutes.reduce((a, r) => a + r.stop_count, 0)} paragens em {dayRoutes.length} veículo(s) · {carrierToday.length} recolhas
          </span>
          {confirmed && <Tag tone="ok">dia confirmado</Tag>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/despacho" className="inline-flex h-8 items-center gap-1 rounded-md border px-2.5 text-[12.5px] hover:border-foreground/30"><MapIcon className="h-3.5 w-3.5" /> Despacho</Link>
          <Link to="/planeamento/quadro" className="inline-flex h-8 items-center gap-1 rounded-md border px-2.5 text-[12.5px] hover:border-foreground/30" title="Quadro dias × veículos e proposta automática"><LayoutGrid className="h-3.5 w-3.5" /> Quadro da semana</Link>
          {canEdit && day && dayRoutes.length > 0 && (
            <AsyncButton
              size="sm"
              variant={confirmed ? "outline" : "default"}
              disabled={busy || (!confirmed && problems)}
              title={!confirmed && problems ? "Há rotas com problemas: resolve antes de confirmar" : undefined}
              onRun={() => run(confirmed ? `${dayShort(day)} reaberto` : `${dayShort(day)} confirmado`, () => confirmDay(shell, day, confirmed))}
            >
              {confirmed ? <><LockOpen className="h-3.5 w-3.5" /> Reabrir o dia</> : <><Lock className="h-3.5 w-3.5" /> Confirmar o dia</>}
            </AsyncButton>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[340px_minmax(0,1fr)]">
        {/* ── por agendar ── */}
        <aside className="flex max-h-[calc(100vh-130px)] flex-col overflow-hidden rounded-xl border bg-card">
          <div className="border-b p-3">
            <b className="text-[14px]">Por agendar</b>
            <div className="text-[12px] text-muted-foreground">Atribuídas no Despacho, ainda sem dia. Marca as que saem neste dia.</div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {groups.map((g) => {
              const target = g.vehicle?.id ?? vehicleId
              const sel = g.items.filter((o) => checked.has(o.id)).map((o) => o.id)
              return (
                <div key={g.key}>
                  <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-1.5">
                    <b className="text-[12.5px]">{g.title}</b>
                    <span className="font-mono text-[11px] text-muted-foreground">{g.items.length}</span>
                    {canEdit && target && day && (
                      <Button size="sm" variant="ghost" className="ml-auto h-6 px-1.5 text-[11.5px]" disabled={busy} onClick={() => void addTo(target, sel.length ? sel : g.items.map((o) => o.id))}>
                        <Plus className="h-3 w-3" /> {sel.length ? `${sel.length} marcada(s)` : "todas"} → {vehicles.find((v) => v.id === target)?.name}
                      </Button>
                    )}
                  </div>
                  {g.items.map((o) => (
                    <label key={o.id} className="grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 border-b px-3 py-1.5 hover:bg-muted/40">
                      <input type="checkbox" disabled={!canEdit} checked={checked.has(o.id)} onChange={() => toggle(o.id)} />
                      <span className="min-w-0">
                        <span className="block truncate text-[12.5px] font-medium">{o.locality ?? "—"} · {o.customer_name ?? "—"}</span>
                        <span className="block truncate font-mono text-[11px] text-muted-foreground">{o.source_ref} · {kg(o.total_kg)} · {m3(o.total_m3)} · prazo {dm(day10(o.due_date))}</span>
                      </span>
                      <span className="flex flex-col items-end gap-0.5">
                        <Tag>{o.load_class ?? "—"}</Tag>
                        {o.needs_tail_lift && <Tag tone="info">plataforma</Tag>}
                      </span>
                    </label>
                  ))}
                </div>
              )
            })}
            {carrierPending.length > 0 && (
              <div>
                <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-1.5">
                  <b className="text-[12.5px]">Transportadora — recolha por marcar</b>
                  <span className="font-mono text-[11px] text-muted-foreground">{carrierPending.length}</span>
                  {canEdit && day && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto h-6 px-1.5 text-[11.5px]"
                      disabled={busy}
                      onClick={() => {
                        const sel = carrierPending.filter((o) => checked.has(o.id)).map((o) => o.id)
                        const ids = sel.length ? sel : carrierPending.map((o) => o.id)
                        void run(`${ids.length} recolha(s) em ${dayShort(day)}`, async () => {
                          for (let i = 0; i < ids.length; i += 60) await setPickupDay(shell, ids.slice(i, i + 60), day)
                          setChecked((p) => { const next = new Set(p); ids.forEach((id) => next.delete(id)); return next })
                        })
                      }}
                    >
                      <Plus className="h-3 w-3" /> recolha neste dia
                    </Button>
                  )}
                </div>
                {carrierPending.map((o) => {
                  const opt = o.options.find((x) => x.service_id === o.carrier_service_id)
                  return (
                    <label key={o.id} className="grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 border-b px-3 py-1.5 hover:bg-muted/40">
                      <input type="checkbox" disabled={!canEdit} checked={checked.has(o.id)} onChange={() => toggle(o.id)} />
                      <span className="min-w-0">
                        <span className="block truncate text-[12.5px] font-medium">{o.locality ?? "—"} · {o.customer_name ?? "—"}</span>
                        <span className="block truncate font-mono text-[11px] text-muted-foreground">{o.source_ref} · {opt?.carrier_name ?? "—"} · recolha prevista {dm(day10(opt?.pickup))}</span>
                      </span>
                      <SageTag o={o} />
                    </label>
                  )
                })}
              </div>
            )}
            {groups.length === 0 && carrierPending.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                Nada por agendar. Atribui encomendas no <Link to="/despacho" className="text-accent underline">Despacho</Link>.
              </div>
            )}
          </div>
        </aside>

        <div className="flex min-w-0 flex-col gap-3">
          {/* ── veículos do dia ── */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
            {vehicles.map((v) => {
              const r = dayRoutes.find((x) => x.vehicle_id === v.id)
              const start = r?.start_min ?? toMin(v.shift_start, 480)
              const end = toMin(v.shift_end, 1080)
              return (
                <button key={v.id} onClick={() => setVehicleId(v.id)} className={cn("flex flex-col gap-1 rounded-xl border bg-card p-2.5 text-left hover:border-foreground/30", vehicleId === v.id && "border-accent ring-1 ring-accent", r?.status === "confirmed" && "bg-green-50")}>
                  <div className="flex items-baseline justify-between gap-2">
                    <b className="text-[13px]">{v.name}</b>
                    <span className="font-mono text-[11.5px] text-muted-foreground">{r ? `${r.stop_count} paragens` : "não sai"}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{v.kind ?? ""}{v.has_tail_lift ? " · plataforma" : ""}</div>
                  {r ? (
                    <>
                      <div className="text-[11px] text-muted-foreground">{n(r.km)?.toLocaleString("pt-PT")} km · volta {hhmm(r.end_min)} · {eur(r.cost_variable)}</div>
                      <Bar label="kg" value={n(r.kg) ?? 0} max={n(v.max_kg) ?? 0} />
                      <Bar label="m³" value={n(r.m3) ?? 0} max={n(v.max_m3) ?? 0} />
                      <Bar label="h" value={(r.end_min ?? start) - start} max={end - start} />
                      <div className="flex flex-wrap gap-1">
                        {r.status === "confirmed" && <Tag tone="ok">confirmada</Tag>}
                        {r.problems?.length > 0 && <Tag tone="bad">{r.problems.length} problema(s)</Tag>}
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] text-muted-foreground">{kg(v.max_kg)} · {m3(v.max_m3)}</div>
                  )}
                </button>
              )
            })}
          </div>

          {/* ── rota do veículo selecionado ── */}
          <div className="rounded-xl border bg-card p-3">
            {!vehicle || !day ? (
              <div className="text-sm text-muted-foreground">Sem veículos ativos.</div>
            ) : !route ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                {vehicle.name} ainda não sai em {dayShort(day)}. Marca encomendas à esquerda e adiciona-as a este veículo; no fim, «Sugerir ordem» calcula a rota.
              </div>
            ) : (
              <RoutePanel
                shell={shell}
                view={view}
                route={route}
                vehicles={vehicles}
                warehouse={warehouse}
                orders={byOrder}
                canEdit={canEdit}
                onReload={load}
                actions={canEdit && route.status !== "confirmed" ? (
                  <AsyncButton size="sm" className="h-7" disabled={busy} title="Melhor ordem só destas paragens (não troca de veículo)" onRun={() => run(`${vehicle.name}: ordem sugerida`, () => scheduleDay(shell, { day, vehicle_id: vehicle.id, optimize: true }))}>
                    <Sparkles className="h-3.5 w-3.5" /> Sugerir ordem
                  </AsyncButton>
                ) : undefined}
              />
            )}
          </div>

          {/* ── recolhas do dia ── */}
          {day && carrierGroups.length > 0 && (
            <div className="rounded-xl border bg-card p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <b className="text-[14px]">Recolhas pela transportadora · {dayShort(day)}</b>
                <AsyncButton size="sm" variant="outline" className="h-7" onRun={() => downloadDocument(shell, "shipping_list", { day }).then(() => undefined).catch((err) => shell.toast("error", "Lista de envios", errorMessage(err)))}>
                  <FileSpreadsheet className="h-3.5 w-3.5" /> Lista de envios (todas)
                </AsyncButton>
              </div>
              <div className="flex flex-col gap-3">
                {carrierGroups.map((g) => (
                  <div key={g.id}>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="text-[13px] font-semibold">{g.name} · {g.items.length} · {eur(g.cost)}</span>
                      <AsyncButton size="sm" variant="ghost" className="h-7" onRun={() => downloadDocument(shell, "shipping_list", { carrier_id: g.id, day }).then(() => undefined).catch((err) => shell.toast("error", "Lista de envios", errorMessage(err)))}>
                        <FileSpreadsheet className="h-3.5 w-3.5" /> Lista de envios
                      </AsyncButton>
                    </div>
                    <div className="flex flex-col divide-y rounded-lg border">
                      {g.items.map((o) => (
                        <div key={o.id} className="flex items-center gap-2 px-2.5 py-1.5 text-[12.5px]">
                          <span className="min-w-0 flex-1 truncate"><b>{o.locality ?? "—"}</b> · {o.customer_name ?? "—"} <span className="font-mono text-muted-foreground">{o.source_ref}</span></span>
                          <span className="font-mono text-[11.5px] text-muted-foreground">{kg(o.total_kg)} · {o.load_class ?? "—"}</span>
                          <SageTag o={o} />
                          {canEdit && (
                            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-red-700" disabled={busy} title="Volta a «recolha por marcar»" onClick={() => void run(`${o.source_ref}: recolha desmarcada`, () => setPickupDay(shell, [o.id], null))}>
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Note>As encomendas entram no fim da rota pela ordem em que as adicionas; «Sugerir ordem» reordena só as paragens deste veículo neste dia, sem as trocar de veículo. «Retirar» numa paragem devolve-a a «Por agendar». A transportadora ou a frota de cada encomenda muda-se no Despacho.</Note>
        </div>
      </div>
    </div>
  )
}
